"use client";

import * as React from "react";
import { useTranslations, useLocale } from "next-intl";
import { CheckCircle2, XCircle } from "lucide-react";
import { formatDate } from "@/shared/dateFormat";
import type { ReportCertificate, ReportStudent } from "@/types/endpoints/reports/GetAssignmentReportInfo";

interface CertificateInfoProps {
  certificate: ReportCertificate;
  student: ReportStudent;
}


export function CertificateInfo({ certificate, student }: CertificateInfoProps) {
  const t = useTranslations();
  const locale = useLocale();

  const isActive = certificate.is_active === null || certificate.is_active === 1;
  const isRevoked = certificate.is_active === 0;

  const formatDateString = (dateString: string) => {
    return formatDate(dateString, locale);
  };

  const getFullName = () => {
    const { user } = student;
    if (!user) return student.name;
    const parts = [user.lastname, user.firstname, user.middlename].filter(Boolean);
    return parts.join(" ");
  };

  return (
    <div className="space-y-4">
      {/* Status Badge */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm text-muted-foreground mb-2">
            {t("label-certificate-status")}
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-700">
                  {t("label-certificate-status-success")}
                </span>
              </>
            )}
            {isRevoked && (
              <>
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="font-medium text-red-700">
                  {t("label-certificate-status-revoked")}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Certificate Number */}
      {/* {certificate.certificate_number && (
        <div className="flex items-baseline gap-2 py-2 border-b border-dotted">
          <div className="text-sm text-muted-foreground bg-white pr-2">
            {t("label-certificate-number")}
          </div>
          <div className="flex-1 border-b border-dotted border-muted-foreground/30"></div>
          <div className="text-sm font-semibold">
            №{certificate.certificate_number}
          </div>
        </div>
      )} */}

      {/* Owner */}
      {student && (
        <div className="flex items-baseline gap-2 py-2 border-b border-dotted">
          <div className="text-sm text-muted-foreground bg-white pr-2">
            {t("label-certificate-owner")}
          </div>
          <div className="flex-1 border-b border-dotted border-muted-foreground/30"></div>
          <div className="text-sm font-semibold">{getFullName()}</div>
        </div>
      )}

      {/* Meta Fields */}
      {certificate.meta && certificate.meta.length > 0 && (
        <>
          {certificate.meta.map((meta, index) => (
            <div
              key={`meta-${index}`}
              className="flex items-baseline gap-2 py-2 border-b border-dotted"
            >
              <div className="text-sm text-muted-foreground bg-white pr-2">
                {meta.field}
              </div>
              <div className="flex-1 border-b border-dotted border-muted-foreground/30"></div>
              <div className="text-sm font-semibold">{meta.value}</div>
            </div>
          ))}
        </>
      )}

      {/* Issued Date */}
      {certificate.issued_at && (
        <div className="flex items-baseline gap-2 py-2 border-b border-dotted">
          <div className="text-sm text-muted-foreground bg-white pr-2">
            {t("label-certificate-issued_at")}
          </div>
          <div className="flex-1 border-b border-dotted border-muted-foreground/30"></div>
          <div className="text-sm font-semibold">
            {formatDateString(certificate.issued_at)}
          </div>
        </div>
      )}
    </div>
  );
}
